import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { resolveNextPath } from '@/lib/authRedirect';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * QA checklist:
 *  1) Email confirm callback with ?redirectTo=/app/producer → lands on /app/producer
 *  2) Google OAuth callback with ?redirectTo=/explore → lands on /explore
 *  3) Callback without ?redirectTo → falls back to /explore
 *  4) Callback with ?redirectTo=https://evil.com → falls back to /explore
 *  5) Failed session → shows error with link back to login
 *  6) Legacy ?next= param still works for backward compat
 */

type CallbackState = 'loading' | 'no-session';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>('loading');

  useEffect(() => {
    const handleCallback = async () => {
      // Resolve intended destination from ?next= query param
      const nextPath = resolveNextPath({
        search: window.location.search,
        isHost: false, // We don't know yet; the destination route will handle further redirects if needed
      });

      // 1) Try to get existing session (works for OTP/magic link)
      let session = null as (Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session']);
      let error: any = null;

      for (let attempt = 0; attempt < 8; attempt++) {
        const result = await supabase.auth.getSession();
        session = result.data.session;
        error = result.error;
        if (session || error) break;
        await new Promise((r) => setTimeout(r, 200));
      }

      // 2) If still no session, try exchangeCodeForSession (OAuth/PKCE)
      if (!session) {
        const code = new URLSearchParams(window.location.search).get('code');
        if (code) {
          const exchanged = await supabase.auth.exchangeCodeForSession(code);
          session = exchanged.data.session;
          error = exchanged.error;
        }
      }

      if (error || !session) {
        setState('no-session');
        return;
      }

      navigate(nextPath, { replace: true });
    };

    handleCallback();
  }, [navigate]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Slutför inloggning...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-2" />
          <CardTitle className="text-lg">Inloggning misslyckades</CardTitle>
          <CardDescription>Kunde inte logga in. Försök igen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button asChild className="w-full">
            <Link to="/login">Tillbaka till inloggning</Link>
          </Button>
          <Button asChild variant="outline" className="w-full">
            <Link to="/">Gå till startsidan</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
